// src/pages/FundPlanner/components/Portfolios/CashFlows.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import ChartTooltip from "components/ChartTooltip";
import ExcelGrid from "components/ExcelGrid";
import { DateInput, DropdownSelect } from "components/InputStyled";
import SectionCard from "components/SectionCard";
import { useProfile } from "context/ProfileContext";
import exportPortfolioCashFlowsPdf from "pages/FundPlanner/reports/exportPortfolioCashFlowsPdf";
import usePortfolioReportPositions from "./usePortfolioReportPositions";
import {
  addYearsIso,
  buildPortfolioCashFlows,
  getLatestScheduledMaturityDate,
} from "./portfolioCashFlows";

const BUCKET_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
];

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatCurrency(value, digits = 0) {
  const n = toNumber(value);
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatAxisCurrency(value) {
  const n = toNumber(value);
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function getNiceTickStep(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;

  const exponent = Math.floor(Math.log10(n));
  const magnitude = 10 ** exponent;
  const normalized = n / magnitude;

  if (normalized <= 1) return magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 2.5) return 2.5 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

function getTightAxisMax(value, tickCount = 5) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;

  const padded = n * 1.05;
  const step = getNiceTickStep(padded / Math.max(1, tickCount - 1));

  return Math.ceil(padded / step) * step;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-input"
      />
      <span>{label}</span>
    </label>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-xs font-medium uppercase text-muted">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-foreground">
        {formatCurrency(value)}
      </div>
    </div>
  );
}

function CashFlowBarChart({ data, pdfMode = false }) {
  const hasPrincipal = data.some((row) => toNumber(row.principal) > 0);
  const hasInterest = data.some((row) => toNumber(row.interest) > 0);
  const hasDemandLiquidity = data.some((row) => toNumber(row.demandLiquidity) > 0);
  const scheduledBarSize = hasDemandLiquidity ? 42 : 64;

  const colors = pdfMode
    ? {
        principal: "#355f18",
        interest: "#6f8f36",
        demandLiquidity: "#d5842a",
        grid: "#d9d9d9",
      }
    : {
        principal: "var(--chart-1)",
        interest: "var(--chart-2)",
        demandLiquidity: "var(--chart-4)",
        grid: "hsl(var(--border))",
      };

  const tooltipFormatter = (value, name) => [formatCurrency(value, 2), name];

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
        <YAxis
          tickFormatter={formatAxisCurrency}
          tick={{ fontSize: 12 }}
          width={72}
          domain={[0, (dataMax) => getTightAxisMax(dataMax)]}
        />
        <Tooltip content={<ChartTooltip formatter={tooltipFormatter} />} />
        <Legend />
        {hasPrincipal ? (
          <Bar
            dataKey="principal"
            stackId="scheduled"
            fill={colors.principal}
            maxBarSize={scheduledBarSize}
            name="Principal"
          />
        ) : null}
        {hasInterest ? (
          <Bar
            dataKey="interest"
            stackId="scheduled"
            fill={colors.interest}
            maxBarSize={scheduledBarSize}
            name="Interest / Income"
          />
        ) : null}
        {hasDemandLiquidity ? (
          <Bar
            dataKey="demandLiquidity"
            fill={colors.demandLiquidity}
            maxBarSize={hasPrincipal || hasInterest ? 42 : 64}
            name="Demand Liquidity"
          />
        ) : null}
      </BarChart>
    </ResponsiveContainer>
  );
}

const cashFlowDetailColumns = [
  {
    key: "date",
    label: "Date",
    width: "min-w-[105px]",
    mono: true,
    date: true,
    copyValue: (row) => formatDate(row.date),
  },
  {
    key: "label",
    label: "Period",
    width: "min-w-[95px]",
  },
  {
    key: "type",
    label: "Type",
    width: "min-w-[135px]",
  },
  {
    key: "investmentType",
    label: "Investment",
    width: "min-w-[175px]",
    render: (row) => row.investmentType || "Unknown",
  },
  {
    key: "fundName",
    label: "Fund",
    width: "min-w-[180px]",
    render: (row) => row.fundName || "",
  },
  {
    key: "cusipOrId",
    label: "CUSIP / ID",
    width: "min-w-[115px]",
    mono: true,
  },
  {
    key: "amount",
    label: "Cash Flow",
    width: "min-w-[120px]",
    mono: true,
    numeric: true,
    className: "text-right",
    render: (row) => formatCurrency(row.amount, 2),
    copyValue: (row) => formatCurrency(row.amount, 2),
  },
];

export default function CashFlows({
  reportScope,
  reportData = null,
  portfolioName = "",
  scopeType = "portfolio",
  scopeName = "",
  showFundColumn = true,
  onExportStateChange,
  settings = null,
  onSettingsChange,
}) {
  const { selectedOrg } = useProfile();
  const cashFlowScope = useMemo(
    () => ({
      ...reportScope,
      reportMode: "single",
      startDate: "",
      endDate: reportScope?.asOfDate,
    }),
    [reportScope]
  );

  const {
    isLoading,
    loadError,
    rows,
    asOfDate,
  } = usePortfolioReportPositions(cashFlowScope, {
    enabled: !reportData,
    data: reportData,
  });

  const [localProjectionEndDate, setLocalProjectionEndDate] = useState("");
  const [localBucketMode, setLocalBucketMode] = useState("monthly");
  const [localIncludeScheduledCashFlows, setLocalIncludeScheduledCashFlows] = useState(true);
  const [localIncludeDemandLiquidity, setLocalIncludeDemandLiquidity] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const hasControlledSettings = settings != null;
  const projectionEndDate = settings?.projectionEndDate ?? localProjectionEndDate;
  const bucketMode = settings?.bucketMode ?? localBucketMode;
  const includeScheduledCashFlows =
    settings?.includeScheduledCashFlows ?? localIncludeScheduledCashFlows;
  const includeDemandLiquidity =
    settings?.includeDemandLiquidity ?? localIncludeDemandLiquidity;

  const updateSetting = useCallback((key, value) => {
    if (hasControlledSettings && onSettingsChange) {
      onSettingsChange((current) => ({ ...current, [key]: value }));
      return;
    }
    if (key === "projectionEndDate") setLocalProjectionEndDate(value);
    if (key === "bucketMode") setLocalBucketMode(value);
    if (key === "includeScheduledCashFlows") setLocalIncludeScheduledCashFlows(value);
    if (key === "includeDemandLiquidity") setLocalIncludeDemandLiquidity(value);
  }, [hasControlledSettings, onSettingsChange]);

  const defaultProjectionEndDate = useMemo(() => {
    return getLatestScheduledMaturityDate(rows, asOfDate) || addYearsIso(asOfDate, 1);
  }, [asOfDate, rows]);

  const safeProjectionEndDate =
    projectionEndDate || defaultProjectionEndDate;

  useEffect(() => {
    updateSetting("projectionEndDate", "");
  }, [asOfDate, updateSetting]);

  const cashFlows = useMemo(
    () =>
      buildPortfolioCashFlows({
        rows,
        asOfDate,
        projectionEndDate: safeProjectionEndDate,
        bucketMode,
        includeScheduledCashFlows,
        includeDemandLiquidity,
      }),
    [
      asOfDate,
      bucketMode,
      includeDemandLiquidity,
      includeScheduledCashFlows,
      rows,
      safeProjectionEndDate,
    ]
  );

  const hasReportData = cashFlows.detailRows.length > 0;
  const frequencyLabel =
    BUCKET_OPTIONS.find((option) => option.value === bucketMode)?.label || "Monthly";

  const onPdfExport = useCallback(async () => {
    try {
      setIsPdfGenerating(true);

      await exportPortfolioCashFlowsPdf({
        orgId: selectedOrg?.id,
        cashFlows,
        asOfDate,
        projectionEndDate: safeProjectionEndDate,
        frequencyLabel,
        orgName: selectedOrg?.name || "",
        portfolioName,
        scopeType,
        scopeName: scopeName || portfolioName,
        showFundColumn,
      });
    } catch (err) {
      console.warn("FundPlanner Cash Flows PDF export failed:", err);
      alert(`PDF export failed: ${err?.message || String(err)}`);
    } finally {
      setIsPdfGenerating(false);
    }
  }, [
    asOfDate,
    cashFlows,
    frequencyLabel,
    portfolioName,
    scopeName,
    scopeType,
    showFundColumn,
    safeProjectionEndDate,
    selectedOrg?.id,
    selectedOrg?.name,
  ]);

  useEffect(() => {
    onExportStateChange?.({
      visible: !isLoading && !loadError && hasReportData,
      disabled: isPdfGenerating,
      label: isPdfGenerating ? "Generating PDF..." : "Export PDF",
      onClick: onPdfExport,
    });
  }, [
    hasReportData,
    isLoading,
    loadError,
    isPdfGenerating,
    onExportStateChange,
    onPdfExport,
  ]);

  useEffect(() => {
    return () => {
      onExportStateChange?.({
        visible: false,
        disabled: true,
        label: "Export PDF",
        onClick: null,
      });
    };
  }, [onExportStateChange]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateInput
          label="Projection End"
          value={safeProjectionEndDate}
          onChange={(value) => updateSetting("projectionEndDate", value)}
          minDate={asOfDate || ""}
          className="min-w-48"
        />
        <DropdownSelect
          label="Frequency"
          value={bucketMode}
          onChange={(value) => updateSetting("bucketMode", value)}
          options={BUCKET_OPTIONS}
          placeholder={null}
          className="min-w-44"
        />
        <div className="flex flex-wrap gap-2">
          <Toggle
            checked={includeScheduledCashFlows}
            onChange={(value) => updateSetting("includeScheduledCashFlows", value)}
            label="Scheduled inflows"
          />
          <Toggle
            checked={includeDemandLiquidity}
            onChange={(value) => updateSetting("includeDemandLiquidity", value)}
            label="Demand liquidity"
          />
        </div>
      </div>

      {isLoading ? (
        <SectionCard title="Cash Flows">
          <div className="text-sm text-muted">Loading cash flow data...</div>
        </SectionCard>
      ) : null}

      {!isLoading && loadError ? (
        <SectionCard title="Cash Flows">
          <div className="text-sm text-red-600 dark:text-red-400">{loadError}</div>
        </SectionCard>
      ) : null}

      {!isLoading && !loadError ? (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            <SummaryTile label="Principal" value={cashFlows.totals.principal} />
            <SummaryTile label="Interest / Income" value={cashFlows.totals.interest} />
            <SummaryTile label="Scheduled Total" value={cashFlows.totals.scheduledTotal} />
            <SummaryTile label="Demand Liquidity" value={cashFlows.totals.demandLiquidity} />
          </div>

          <SectionCard
            title="Projected Cash Flows"
            subtitle={`${formatDate(asOfDate)} through ${formatDate(safeProjectionEndDate)}`}
          >
            {cashFlows.chartRows.length > 0 ? (
              <div className="h-96">
                <CashFlowBarChart data={cashFlows.chartRows} />
              </div>
            ) : (
              <div className="rounded-lg border border-border p-4 text-sm text-muted">
                No cash flows were found for the selected projection and toggles.
              </div>
            )}
          </SectionCard>

          {cashFlows.notes.length > 0 ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
              {cashFlows.notes.slice(0, 4).map((note) => (
                <div key={note}>{note}</div>
              ))}
              {cashFlows.notes.length > 4 ? (
                <div>{cashFlows.notes.length - 4} additional positions need review.</div>
              ) : null}
            </div>
          ) : null}

          <SectionCard title="Cash Flow Detail">
            {cashFlows.detailRows.length > 0 ? (
              <ExcelGrid
                columns={showFundColumn
                  ? cashFlowDetailColumns
                  : cashFlowDetailColumns.filter((column) => column.key !== "fundName")}
                rows={cashFlows.detailRows}
                onChange={() => {}}
              />
            ) : (
              <div className="rounded-lg border border-border p-4 text-sm text-muted">
                No detail rows to display.
              </div>
            )}
          </SectionCard>
        </>
      ) : null}
    </div>
  );
}
